import { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { FeedbackModal } from '../components/shared/FeedbackModal';
import { ShareButton } from '../components/shared/ShareButton';
import { CurrencyTooltip } from '../components/shared/CurrencyTooltip';
import { FormulaDisclosure } from '../components/shared/FormulaDisclosure';
import { DiscountModifier } from '../components/shared/DiscountModifier';
import { MobileCostBar } from '../components/shared/MobileCostBar';
import { CalculatorContentWrapper } from '../components/shared/CalculatorContentWrapper';
import { ScenarioPresets } from '../components/shared/ScenarioPresets';
import { useCalculatorStore } from '../store/calculatorStore';
import { Link } from 'react-router-dom';

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:8787';

// ── Types ────────────────────────────────────────────────────────────────────

interface VmSku {
  sku: string;
  name: string;
  vcpu: number;
  ram: number;
  family: string;
  description: string;
}

interface VmPriceResult {
  sku: string;
  os: 'linux' | 'windows';
  payg: number;
  spot: number;
  monthly_payg: number;
  monthly_spot: number;
}

type OsType = 'linux' | 'windows';
type PricingModel = 'payg' | 'spot';
type LoadState = 'idle' | 'loading' | 'error';

// ── Helpers ──────────────────────────────────────────────────────────────────

const HOURS_PRESETS = [
  { label: 'Always on', hours: 730 },
  { label: 'Business hours', hours: 176 },
  { label: 'Dev/test', hours: 80 },
];

function fmt(n: number, decimals = 2): string {
  return n.toLocaleString('en-GB', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

// ── VM Size Selector ─────────────────────────────────────────────────────────

function VmSelector({ skus, selected, onSelect }: {
  skus: VmSku[];
  selected: VmSku | null;
  onSelect: (sku: VmSku) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = query.trim()
    ? skus.filter((s) =>
        s.name.toLowerCase().includes(query.toLowerCase()) ||
        s.sku.toLowerCase().includes(query.toLowerCase()) ||
        s.family.toLowerCase().includes(query.toLowerCase()) ||
        s.description.toLowerCase().includes(query.toLowerCase()),
      )
    : skus;

  const grouped = filtered.reduce<Record<string, VmSku[]>>((acc, s) => {
    if (!acc[s.family]) acc[s.family] = [];
    acc[s.family].push(s);
    return acc;
  }, {});

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between border rounded px-3 py-2 text-left focus:outline-none focus:border-[#0078d4] transition-colors"
        style={{ backgroundColor: 'var(--surface-container-low)', borderColor: 'var(--outline-variant)' }}
      >
        {selected ? (
          <span className="flex items-center gap-3">
            <span className="text-[13px] font-semibold" style={{ color: 'var(--text-secondary)' }}>{selected.name}</span>
            <span className="text-[11px]" style={{ color: 'var(--text-tertiary)' }}>{selected.vcpu} vCPU · {selected.ram} GB RAM · {selected.description}</span>
          </span>
        ) : (
          <span className="text-[13px]" style={{ color: 'var(--text-tertiary)' }}>Select a VM size…</span>
        )}
        <span className="material-symbols-outlined text-[18px] ml-2 shrink-0" style={{ color: 'var(--text-tertiary)' }}>expand_more</span>
      </button>

      {open && (
        <div className="absolute z-20 top-full left-0 right-0 mt-1 border rounded shadow-2xl max-h-72 overflow-y-auto" style={{ backgroundColor: 'var(--surface-container)', borderColor: 'var(--outline-variant)' }}>
          <div className="sticky top-0 p-2" style={{ backgroundColor: 'var(--surface-container)', borderBottom: '1px solid var(--surface-container-high)' }}>
            <input
              autoFocus
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search VM sizes…"
              className="w-full text-[13px] px-3 py-1.5 rounded focus:outline-none focus:border-[#0078d4]"
              style={{ backgroundColor: 'var(--surface-container-low)', border: '1px solid var(--outline-variant)', color: 'var(--text-secondary)' }}
            />
          </div>
          {Object.entries(grouped).map(([family, items]) => (
            <div key={family}>
              <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest" style={{ backgroundColor: 'var(--surface-container-low)', color: 'var(--text-tertiary)' }}>
                {family}
              </div>
              {items.map((s) => (
                <button
                  key={s.sku}
                  type="button"
                  onClick={() => { onSelect(s); setOpen(false); setQuery(''); }}
                  className="w-full text-left px-3 py-2.5 flex items-center justify-between transition-colors"
                  style={{
                    backgroundColor: selected?.sku === s.sku ? 'var(--surface-container-high)' : 'transparent',
                    color: selected?.sku === s.sku ? '#c7e0f4' : 'var(--text-secondary)'
                  }}
                  onMouseEnter={(e) => {
                    if (selected?.sku !== s.sku) e.currentTarget.style.backgroundColor = 'var(--surface-container-high)';
                  }}
                  onMouseLeave={(e) => {
                    if (selected?.sku !== s.sku) e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <div>
                    <span className="text-[13px] font-semibold">{s.name}</span>
                    <span className="text-[11px] ml-2" style={{ color: 'var(--text-tertiary)' }}>{s.description}</span>
                  </div>
                  <span className="text-[11px] shrink-0 ml-2" style={{ color: 'var(--text-tertiary)' }}>{s.vcpu}C · {s.ram}G</span>
                </button>
              ))}
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="px-3 py-4 text-[13px] text-center" style={{ color: 'var(--text-tertiary)' }}>No VMs match "{query}"</div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main Page ────────────────────────────────────────────────────────────────

export default function VMCalculatorPage() {
  const [skus, setSkus] = useState<VmSku[]>([]);
  const [selected, setSelected] = useState<VmSku | null>(null);
  const [os, setOs] = useState<OsType>('linux');
  const [vmCount, setVmCount] = useState(1);
  const [hours, setHours] = useState(730);
  const [pricingModel, setPricingModel] = useState<PricingModel>('payg');
  const [prices, setPrices] = useState<VmPriceResult | null>(null);
  const [loadState, setLoadState] = useState<LoadState>('idle');
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const { discount, setDiscount } = useCalculatorStore();

  // Load VM list on mount
  useEffect(() => {
    fetch(`${API_BASE}/api/vm-search?q=`)
      .then((r) => r.json())
      .then((d: { vms: VmSku[] }) => {
        setSkus(d.vms ?? []);
        const def = d.vms?.find((v: VmSku) => v.sku === 'Standard_D2s_v5') ?? d.vms?.[0];
        if (def) setSelected(def);
      })
      .catch(() => {});
  }, []);

  // Fetch prices when selection or OS changes
  useEffect(() => {
    if (!selected) return;
    setLoadState('loading');
    setPrices(null);
    fetch(`${API_BASE}/api/vm-prices?sku=${encodeURIComponent(selected.sku)}&os=${os}`)
      .then((r) => r.json())
      .then((d: VmPriceResult) => {
        setPrices(d);
        setLoadState('idle');
      })
      .catch(() => setLoadState('error'));
  }, [selected, os]);

  const hourlyRate = prices
    ? (pricingModel === 'spot' && prices.spot > 0 ? prices.spot : prices.payg)
    : 0;

  const monthlyPerVm = hourlyRate * hours;
  const monthlyTotal = monthlyPerVm * vmCount;

  const monthlySavingIfSpot = prices && prices.spot > 0
    ? (prices.payg - prices.spot) * hours * vmCount
    : 0;

  const estimateLabel = `£${fmt(monthlyTotal)}/mo`;

  return (
    <div
      className="min-h-screen"
      style={{ backgroundColor: 'var(--bg-base)' }}
    >
      <Helmet>
        <title>Azure VM Cost Calculator UK | AzureCalc.uk</title>
        <meta name="description" content="Azure Virtual Machine pricing UK 2026. Compare D-series, B-series, E-series VM costs in GBP for UK South. PAYG vs Spot pricing with live rates." />
        <link rel="canonical" href="https://www.azure-calc.co.uk/virtual-machines" />
        <meta property="og:title" content="Azure VM Cost Calculator UK" />
        <meta property="og:description" content="Compare Azure VM costs in GBP for UK South. PAYG vs Spot pricing, updated nightly." />
        <meta property="og:url" content="https://www.azure-calc.co.uk/virtual-machines" />
        <meta property="og:image" content="https://www.azure-calc.co.uk/og/azure-calc-og.png" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebApplication',
          name: 'Azure VM Cost Calculator UK',
          description: 'Azure Virtual Machine pricing UK 2026. Compare VM costs in GBP for UK South.',
          url: 'https://www.azure-calc.co.uk/virtual-machines',
          applicationCategory: 'FinanceApplication',
          operatingSystem: 'Web',
          offers: { '@type': 'Offer', price: '0', priceCurrency: 'GBP' },
        })}</script>
      </Helmet>

      <div className="max-w-[1100px] mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-[28px] font-bold leading-tight mb-1" style={{ color: 'var(--text-primary)' }}>
            Azure VM Cost Calculator
          </h1>
          <p className="text-[14px]" style={{ color: 'var(--text-tertiary)' }}>
            Live pricing for Virtual Machines in UK South (GBP). Prices fetched directly from the Azure Retail Prices API.
          </p>
        </div>

        <ScenarioPresets presets={[
          {
            label: 'Dev / test VM',
            description: 'B2ms · 160 hrs/mo · Linux — typical dev/test burstable',
            apply: () => { setOs('linux'); setHours(160); setSelected(skus.find(s => s.sku === 'Standard_B2ms') ?? null); },
          },
          {
            label: 'Production web server',
            description: 'D2s v3 · always-on · Linux — standard general-purpose production',
            apply: () => { setOs('linux'); setHours(730); setSelected(skus.find(s => s.sku === 'Standard_D2s_v3') ?? null); },
          },
          {
            label: 'Windows app server',
            description: 'D4s v3 · always-on · Windows — shows Windows licence premium',
            apply: () => { setOs('windows'); setHours(730); setSelected(skus.find(s => s.sku === 'Standard_D4s_v3') ?? null); },
          },
        ]} />

        <div className="flex flex-col lg:flex-row gap-6">

          {/* ── LEFT: Inputs ── */}
          <div className="flex-[3] flex flex-col gap-4">

            {/* VM Size */}
            <div className="rounded-lg p-5" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}>
              <label className="block text-[11px] font-bold uppercase tracking-wide mb-2" style={{ color: 'var(--text-tertiary)' }}>
                VM Size
              </label>
              <VmSelector skus={skus} selected={selected} onSelect={setSelected} />
              {selected && (
                <div className="mt-2 flex gap-4 text-[11px]" style={{ color: 'var(--text-tertiary)' }}>
                  <span>⚙️ {selected.vcpu} vCPU</span>
                  <span>🧠 {selected.ram} GB RAM</span>
                  <span>📦 {selected.family}</span>
                  <span>{selected.description}</span>
                </div>
              )}
            </div>

            {/* OS toggle */}
            <div className="rounded-lg p-5" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}>
              <label className="block text-[11px] font-bold uppercase tracking-wide mb-3" style={{ color: 'var(--text-tertiary)' }}>
                Operating System
              </label>
              <div className="flex gap-2">
                {(['linux', 'windows'] as OsType[]).map((o) => (
                  <button
                    key={o}
                    onClick={() => setOs(o)}
                    className={[
                      'flex-1 py-2.5 rounded-lg border text-[13px] font-semibold transition-all flex items-center justify-center gap-2',
                      os === o
                        ? 'bg-[#0078d4] border-[#0078d4] text-white'
                        : 'hover:border-[#0078d4]/50',
                    ].join(' ')}
                    style={os === o ? {} : { backgroundColor: 'var(--surface-container-low)', borderColor: 'var(--outline-variant)', color: 'var(--text-tertiary)' }}
                  >
                    <span>{o === 'linux' ? '🐧' : '🪟'}</span>
                    {o === 'linux' ? 'Linux' : 'Windows Server'}
                  </button>
                ))}
              </div>
              {os === 'windows' && (
                <p className="mt-2 text-[11px]" style={{ color: 'var(--text-tertiary)' }}>
                  Windows Server licensing is included in the Microsoft pricing shown.
                </p>
              )}
            </div>

            {/* Count */}
            <div className="rounded-lg p-5" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}>
              <label className="block text-[11px] font-bold uppercase tracking-wide mb-2" style={{ color: 'var(--text-tertiary)' }}>
                Number of VMs
              </label>
              <input
                type="number"
                min={1}
                max={1000}
                value={vmCount}
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10);
                  if (!isNaN(v) && v >= 1 && v <= 1000) setVmCount(v);
                }}
                className="w-28 text-[15px] px-3 py-2 rounded focus:outline-none focus:border-[#0078d4] font-mono"
                style={{ backgroundColor: 'var(--surface-container-low)', border: '1px solid var(--outline-variant)', color: 'var(--text-secondary)' }}
              />
            </div>

            {/* Hours */}
            <div className="rounded-lg p-5" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}>
              <div className="flex items-center justify-between mb-3">
                <label className="text-[11px] font-bold uppercase tracking-wide" style={{ color: 'var(--text-tertiary)' }}>
                  Hours per Month
                </label>
                <span className="text-[20px] font-bold font-mono text-[#0078d4]">{hours}h</span>
              </div>
              <input
                type="range"
                min={1}
                max={730}
                value={hours}
                onChange={(e) => setHours(parseInt(e.target.value, 10))}
                className="w-full accent-[#0078d4] mb-3"
              />
              <div className="flex gap-2">
                {HOURS_PRESETS.map((p) => (
                  <button
                    key={p.hours}
                    onClick={() => setHours(p.hours)}
                    className={[
                      'flex-1 py-1.5 rounded text-[11px] font-semibold border transition-all',
                      hours === p.hours
                        ? 'border-[#0078d4] text-[#0078d4]'
                        : '',
                    ].join(' ')}
                    style={hours === p.hours
                      ? { backgroundColor: 'var(--surface-container)', borderColor: '#0078d4', color: '#0078d4', boxShadow: 'inset 0 0 0 1px #0078d4' }
                      : { backgroundColor: 'var(--surface-container-low)', borderColor: 'var(--outline-variant)', color: 'var(--text-tertiary)' }}
                  >
                    {p.label}
                    <span className="block text-[9px] opacity-70">{p.hours}h</span>
                  </button>
                ))}
              </div>
              {hours < 730 && (
                <p className="mt-2 text-[11px]" style={{ color: 'var(--text-tertiary)' }}>
                  💡 For always-on workloads, Reserved Instances (1 or 3-year) can save up to 72% vs PAYG.
                </p>
              )}
            </div>

            {/* Pricing model */}
            <div className="rounded-lg p-5" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}>
              <label className="block text-[11px] font-bold uppercase tracking-wide mb-3" style={{ color: 'var(--text-tertiary)' }}>
                Pricing Model
              </label>
              <div className="flex gap-2">
                {(['payg', 'spot'] as PricingModel[]).map((m) => (
                  <button
                    key={m}
                    onClick={() => setPricingModel(m)}
                    className={[
                      'flex-1 py-2.5 rounded-lg border text-[13px] font-semibold transition-all',
                      pricingModel === m
                        ? 'bg-[#0078d4] border-[#0078d4] text-white'
                        : 'hover:border-[#0078d4]/50',
                    ].join(' ')}
                    style={pricingModel === m ? {} : { backgroundColor: 'var(--surface-container-low)', borderColor: 'var(--outline-variant)', color: 'var(--text-tertiary)' }}
                  >
                    {m === 'payg' ? 'Pay-as-you-go' : 'Spot'}
                  </button>
                ))}
              </div>
              {pricingModel === 'spot' && (
                <div className="mt-3 rounded p-3" style={{ backgroundColor: 'var(--surface-container-lowest)', border: '1px solid rgba(232, 176, 0, 0.4)' }}>
                  <p className="text-[12px]" style={{ color: '#e8b000' }}>
                    ⚠️ <strong>Spot VMs can be evicted at any time</strong> — not suitable for production workloads. Use only for fault-tolerant, interruptible batch jobs.
                  </p>
                </div>
              )}
            </div>

          </div>

          {/* ── RIGHT: Results ── */}
          <div className="flex-[2] min-w-0 lg:max-w-[400px]">
            <div className="sticky top-20 flex flex-col gap-4">

              {/* Monthly estimate */}
              <div id="vm-results" className="rounded-lg p-5" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}>
                <p className="text-[11px] font-bold uppercase tracking-wide mb-4" style={{ color: 'var(--text-tertiary)' }}>
                  Monthly Estimate
                </p>

                {loadState === 'loading' && (
                  <div className="flex flex-col gap-2">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="h-4 rounded animate-pulse" style={{ width: `${70 - i * 10}%`, backgroundColor: 'var(--surface-container-high)' }} />
                    ))}
                  </div>
                )}

                {loadState === 'error' && (
                  <div className="rounded p-3 text-[12px]" style={{ backgroundColor: 'var(--surface-container-lowest)', border: '1px solid rgba(164, 38, 44, 0.4)', color: '#a4262c' }}>
                    Failed to fetch prices. Please try another VM size or check your connection.
                  </div>
                )}

                {prices && loadState === 'idle' && (
                  <>
                    <div className="flex items-baseline gap-1 mb-1">
                      <span className="text-[36px] font-bold font-mono text-[#0078d4]">
                        £{fmt(monthlyTotal)}
                      </span>
                      <CurrencyTooltip />
                    </div>
                    <p className="text-[12px] mb-4" style={{ color: 'var(--text-tertiary)' }}>per month (estimate)</p>

                    <FormulaDisclosure
                      lines={[
                        `£${fmt(hourlyRate, 4)}/hr × ${hours}h × ${vmCount} VM${vmCount !== 1 ? 's' : ''} = £${fmt(monthlyTotal)}/mo`,
                        `Model: ${pricingModel.toUpperCase()} · OS: ${os === 'linux' ? 'Linux' : 'Windows Server'} · SKU: ${selected?.sku ?? ''}`,
                        `Region: UK South · Prices from Azure Retail Prices API`,
                      ]}
                      fetchedAt={new Date().toISOString()}
                    />

                    {/* Breakdown */}
                    <div className="flex flex-col gap-1.5 text-[12px] pt-4" style={{ borderTop: '1px solid var(--surface-container-high)' }}>
                      {[
                        { label: 'Hourly rate', value: `£${fmt(hourlyRate, 4)}/hr` },
                        { label: 'Hours per month', value: `${hours}h` },
                        { label: 'VMs', value: `× ${vmCount}` },
                        { label: 'Per VM / month', value: `£${fmt(monthlyPerVm)}` },
                      ].map((r) => (
                        <div key={r.label} className="flex justify-between">
                          <span style={{ color: 'var(--text-tertiary)' }}>{r.label}</span>
                          <span className="font-mono" style={{ color: 'var(--text-secondary)' }}>{r.value}</span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>

              {/* Comparison table */}
              {prices && loadState === 'idle' && (
                <div className="rounded-lg overflow-hidden" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}>
                  <div className="px-4 py-2.5" style={{ borderBottom: '1px solid var(--outline-variant)' }}>
                    <p className="text-[11px] font-bold uppercase tracking-wide" style={{ color: 'var(--text-tertiary)' }}>Pricing Comparison</p>
                  </div>
                  <table className="w-full text-[12px]">
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--surface-container-high)' }}>
                        <th className="px-3 py-2 text-left text-[10px] font-bold uppercase tracking-wide" style={{ color: 'var(--text-tertiary)' }}>Model</th>
                        <th className="px-3 py-2 text-right text-[10px] font-bold uppercase tracking-wide" style={{ color: 'var(--text-tertiary)' }}>Hourly</th>
                        <th className="px-3 py-2 text-right text-[10px] font-bold uppercase tracking-wide" style={{ color: 'var(--text-tertiary)' }}>1 VM / mo</th>
                        <th className="px-3 py-2 text-right text-[10px] font-bold uppercase tracking-wide" style={{ color: 'var(--text-tertiary)' }}>{vmCount} VM{vmCount !== 1 ? 's' : ''} / mo</th>
                      </tr>
                    </thead>
                    <tbody style={{ borderTop: '1px solid var(--surface-container-low)' }}>
                      <tr style={{ backgroundColor: pricingModel === 'payg' ? 'var(--surface-container-low)' : 'transparent' }}>
                        <td className="px-3 py-2.5 font-semibold" style={{ color: 'var(--text-secondary)' }}>PAYG</td>
                        <td className="px-3 py-2.5 text-right font-mono" style={{ color: 'var(--text-secondary)' }}>£{fmt(prices.payg, 4)}</td>
                        <td className="px-3 py-2.5 text-right font-mono" style={{ color: 'var(--text-secondary)' }}>£{fmt(prices.payg * hours)}</td>
                        <td className="px-3 py-2.5 text-right text-[#0078d4] font-mono font-semibold">£{fmt(prices.payg * hours * vmCount)}</td>
                      </tr>
                      <tr style={{ backgroundColor: pricingModel === 'spot' ? 'var(--surface-container-low)' : 'transparent' }}>
                        <td className="px-3 py-2.5 font-semibold" style={{ color: 'var(--text-secondary)' }}>Spot</td>
                        {prices.spot > 0 ? (
                          <>
                            <td className="px-3 py-2.5 text-right font-mono" style={{ color: 'var(--text-secondary)' }}>£{fmt(prices.spot, 4)}</td>
                            <td className="px-3 py-2.5 text-right font-mono" style={{ color: 'var(--text-secondary)' }}>£{fmt(prices.spot * hours)}</td>
                            <td className="px-3 py-2.5 text-right text-[#107c10] font-mono font-semibold">£{fmt(prices.spot * hours * vmCount)}</td>
                          </>
                        ) : (
                          <td colSpan={3} className="px-3 py-2.5 text-center" style={{ color: 'var(--text-tertiary)' }}>Not available for this SKU</td>
                        )}
                      </tr>
                    </tbody>
                  </table>
                  <div className="px-4 py-2.5 text-[10px]" style={{ borderTop: '1px solid var(--surface-container-high)', color: 'var(--text-tertiary)' }}>
                    Prices fetched live from Azure Retail Prices API · UK South · GBP · Spot prices fluctuate
                  </div>
                </div>
              )}

              {/* Annual projection */}
              {prices && loadState === 'idle' && (
                <div className="rounded-lg p-4 text-[12px]" style={{ backgroundColor: 'var(--surface-container-low)', border: '1px solid var(--surface-container-high)' }}>
                  <p className="text-[11px] font-bold uppercase tracking-wide mb-3" style={{ color: 'var(--text-tertiary)' }}>Annual Projection</p>
                  <div className="flex justify-between mb-1.5">
                    <span style={{ color: 'var(--text-tertiary)' }}>PAYG annual ({vmCount} VM{vmCount !== 1 ? 's' : ''})</span>
                    <span className="font-mono font-semibold" style={{ color: 'var(--text-secondary)' }}>£{fmt(prices.payg * hours * vmCount * 12)}</span>
                  </div>
                  {monthlySavingIfSpot > 0 && (
                    <div className="flex justify-between">
                      <span style={{ color: '#107c10' }}>Potential spot saving</span>
                      <span className="font-mono font-semibold" style={{ color: '#107c10' }}>−£{fmt(monthlySavingIfSpot * 12)}/yr</span>
                    </div>
                  )}
                  {hours === 730 && (
                    <div className="mt-3 pt-3" style={{ borderTop: '1px solid var(--surface-container-high)', color: 'var(--text-tertiary)' }}>
                      💡 Always-on workloads: a 1-year Reserved Instance can save ~40%; 3-year ~60%.
                    </div>
                  )}
                </div>
              )}

              {/* Share + Feedback */}
              {prices && loadState === 'idle' && (
                <div className="flex flex-col gap-2">
                  <ShareButton
                    service="virtual-machines"
                    inputs={{ sku: selected?.sku, os, vmCount, hours, pricingModel }}
                    result={{ monthly_estimate: monthlyTotal, hourly: hourlyRate, payg: prices.payg, spot: prices.spot }}
                  />
                  <a href="https://azure.microsoft.com/en-gb/pricing/details/virtual-machines/linux/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-[10px] font-mono" style={{ color: 'var(--text-tertiary)' }}>
                    <span className="material-symbols-outlined text-[13px]">open_in_new</span>
                    Verify on official Azure pricing page
                  </a>
                  <button
                    onClick={() => setFeedbackOpen(true)}
                    className="flex items-center justify-center gap-2 w-full py-2 rounded border text-[12px] transition-colors"
                    style={{ borderColor: 'var(--outline-variant)', color: 'var(--text-tertiary)' }}
                  >
                    <span className="material-symbols-outlined text-[15px]">flag</span>
                    Report an issue
                  </button>
                </div>
              )}

              {/* Resources */}
              <div className="border rounded" style={{ borderColor: 'var(--outline-variant)' }}>
                <details>
                  <summary className="flex items-center justify-between px-4 py-3 cursor-pointer transition-colors list-none">
                    <span className="flex items-center gap-2 text-[12px] font-semibold uppercase tracking-wider" style={{ color: 'var(--text-tertiary)' }}>
                      <span className="material-symbols-outlined text-[16px]">link</span>
                      Sources &amp; Resources
                    </span>
                    <span className="material-symbols-outlined text-[18px]" style={{ color: 'var(--text-tertiary)' }}>expand_more</span>
                  </summary>
                  <div style={{ borderTop: '1px solid var(--outline-variant)' }}>
                    {[
                      { label: 'Azure Pricing', title: 'Azure VM pricing (Linux)', url: 'https://azure.microsoft.com/en-gb/pricing/details/virtual-machines/linux/' },
                      { label: 'Azure Pricing', title: 'Azure VM pricing (Windows)', url: 'https://azure.microsoft.com/en-gb/pricing/details/virtual-machines/windows/' },
                      { label: 'Microsoft Learn', title: 'Virtual machine sizes overview', url: 'https://learn.microsoft.com/en-us/azure/virtual-machines/sizes/overview' },
                    ].map((r) => (
                      <a key={r.url} href={r.url} target="_blank" rel="noopener noreferrer"
                        className="group flex items-center gap-3 px-4 py-3 transition-colors">
                        <span className={`shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded border uppercase tracking-wider ${r.label === 'Microsoft Learn' ? 'text-[#107c10] border-[#107c10]/40' : 'text-[#0078d4] border-[#0078d4]/40'}`} style={{ backgroundColor: 'var(--surface-container-low)' }}>
                          {r.label}
                        </span>
                        <span className="flex-1 text-[13px] leading-snug" style={{ color: 'var(--text-secondary)' }}>{r.title}</span>
                        <span className="material-symbols-outlined text-[#0078d4] text-[16px] shrink-0">open_in_new</span>
                      </a>
                    ))}
                  </div>
                </details>
              </div>

            </div>
          </div>

        </div>

        {monthlyTotal > 0 && (
          <DiscountModifier
            discount={discount}
            onDiscountChange={setDiscount}
            paygTotal={monthlyTotal}
          />
        )}

        <CalculatorContentWrapper
          eyebrow="Compute Pricing"
          title="What drives Azure VM cost in practice"
          intro={
            <>
              <p className="mb-3">
                Virtual machine pricing looks simple until a team starts comparing sizes, operating
                systems, availability expectations, and procurement models. The hourly rate is the
                visible part of the bill, but the useful budgeting questions are usually about workload
                fit: whether the SKU is oversized, whether Windows licensing is necessary, and whether
                an always-on server should still be on pay-as-you-go pricing.
              </p>
              <p>
                This page keeps the calculator first and adds the decision context around it. The aim
                is to help you interpret the estimate, not to bury the tool under generic content.
              </p>
            </>
          }
          sections={[
            {
              id: 'billing-drivers',
              title: 'The main VM billing drivers are size, runtime, and procurement model',
              body: (
                <>
                  <p className="mb-3">
                    Azure VM compute cost is driven primarily by the selected SKU, the number of hours
                    the machine runs, and whether you buy capacity as pay-as-you-go, Spot, or a longer
                    commitment such as Reserved Instances. A larger D-series or E-series VM can be the
                    right technical choice, but it also raises cost every hour the instance remains
                    powered on.
                  </p>
                  <p>
                    That is why this calculator exposes the VM size, hours per month, and pricing model
                    directly. Those are the inputs that most quickly separate a realistic compute budget
                    from a rough guess.
                  </p>
                </>
              ),
            },
            {
              id: 'region-variance',
              title: 'Region and operating system choices change the baseline',
              body: (
                <>
                  <p className="mb-3">
                    The numbers here are based on UK South pricing in GBP. That matters because Azure
                    compute rates are not uniform across regions. If your architecture can deploy in
                    multiple regions, comparing the same SKU across locations can reveal a meaningful
                    difference before storage, bandwidth, backup, or monitoring are added.
                  </p>
                  <p>
                    Operating system choice matters too. Linux pricing usually reflects compute alone,
                    while Windows Server rates include the Microsoft licence component. If you only look
                    at the instance family and ignore OS, you can understate cost materially.
                  </p>
                </>
              ),
            },
            {
              id: 'os-licensing',
              title: 'OS licensing and Hybrid Benefit are easy to overlook',
              body: (
                <>
                  <p className="mb-3">
                    Many teams spot the compute delta between Linux and Windows but forget to ask why it
                    exists. Windows Server VM pricing includes software licensing, so the hourly rate is
                    not just a hardware proxy. If your organisation is eligible for Azure Hybrid Benefit,
                    the invoice can differ from the simple retail view shown here.
                  </p>
                  <p>
                    That does not make the estimate less useful. It means the calculator is a strong
                    retail benchmark and a planning tool, while agreement-specific discounts or licence
                    entitlements should be validated before procurement.
                  </p>
                </>
              ),
            },
            {
              id: 'worked-example',
              title: 'Worked example: the cost gap between dev/test and always-on production',
              body: (
                <>
                  <p className="mb-3">
                    Consider a pair of <code>Standard_D2s_v5</code> virtual machines. At business-hours
                    usage, the monthly total may look manageable because you are only paying for roughly
                    176 hours. Push the same pair to 730 hours for an always-on production workload and
                    the annual projection changes immediately, even before you account for disks,
                    backups, and log ingestion.
                  </p>
                  <p>
                    That is the real value of this calculator: you can test whether your main lever is
                    schedule control, SKU selection, or procurement strategy instead of treating compute
                    spend as fixed.
                  </p>
                </>
              ),
            },
            {
              id: 'optimisation',
              title: 'Practical optimisation strategies for VM-heavy estates',
              body: (
                <ul className="list-disc pl-5 space-y-2">
                  <li>Rightsize first. VM oversizing is still one of the fastest ways to waste Azure budget.</li>
                  <li>Use shutdown schedules for non-production machines instead of letting dev and test run all month.</li>
                  <li>Review whether burstable or smaller SKUs are sufficient before defaulting to general-purpose D-series.</li>
                  <li>Move stable always-on workloads to Reserved Instances or Savings Plans rather than leaving them on retail PAYG.</li>
                  <li>Treat Spot as an optimisation for interruptible jobs only, not as a baseline assumption for production hosting.</li>
                </ul>
              ),
            },
            {
              id: 'pitfalls',
              title: 'Common VM pricing pitfalls',
              body: (
                <ul className="list-disc pl-5 space-y-2">
                  <li>Comparing only vCPU count and ignoring RAM, family capabilities, or licensing differences.</li>
                  <li>Budgeting the compute line but forgetting managed disks, snapshots, backups, networking, and monitoring.</li>
                  <li>Using PAYG figures for workloads that already have predictable multi-year demand.</li>
                  <li>Assuming Spot availability and eviction behavior are acceptable without verifying workload resilience.</li>
                  <li>Estimating one VM accurately and then scaling the fleet without checking whether every machine really needs the same SKU.</li>
                </ul>
              ),
            },
            {
              id: 'faq',
              title: 'Quick FAQ',
              body: (
                <>
                  <p className="mb-2"><strong>Why does Windows cost more than Linux?</strong> The Windows rate includes Microsoft licensing on top of the compute service.</p>
                  <p className="mb-2"><strong>Are Spot prices safe for budgeting?</strong> Only for fault-tolerant workloads that can tolerate eviction and price fluctuation.</p>
                  <p><strong>What else should you add after using this calculator?</strong> Managed disks, backup, outbound bandwidth, log analytics, and any agreement-specific discounts.</p>
                </>
              ),
            },
          ]}
          methodologyNote={
            <>
              <p className="mb-2">
                AzureCalc.uk uses UK South retail pricing in GBP and shows the estimate formula on the
                page. For live data sourcing, refresh cadence, and agreement caveats, review the
                methodology notes before turning a calculator output into a purchase decision.
              </p>
              <Link to="/methodology" className="text-[#0078d4] hover:underline">
                Review pricing methodology →
              </Link>
            </>
          }
          relatedLinks={[
            { label: 'Bandwidth Calculator', to: '/bandwidth' },
            { label: 'App Service Calculator', to: '/app-service' },
            { label: 'Guided Cost Estimator', to: '/guided' },
            { label: 'Methodology', to: '/methodology' },
          ]}
        />

        {/* Disclaimer */}
        <div className="mt-8 p-4 border rounded text-[11px] leading-relaxed" style={{ borderColor: 'var(--outline-variant)', color: 'var(--text-tertiary)' }}>
          DISCLAIMER: Cost estimates are approximations only. Prices are fetched live from the Microsoft Azure Retail Prices API for UK South (uksouth) in GBP. Actual costs may vary based on your Microsoft agreement, reserved instances, hybrid benefit eligibility, and usage patterns. Always verify through the official Azure Pricing Calculator before making purchasing decisions.
        </div>
      </div>

      {feedbackOpen && (
        <FeedbackModal
          service="virtual-machines"
          estimateValue={estimateLabel}
          onClose={() => setFeedbackOpen(false)}
        />
      )}

      <MobileCostBar
        amount={monthlyTotal > 0 ? monthlyTotal : null}
        label="Virtual Machines"
        targetId="vm-results"
      />
    </div>
  );
}
